import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Grid, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';

import { ApiParams } from '../../../common/api/api';
import { useDebounce } from '../../../common/Debounce.hooks';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { useIdSearchableFilterOptions } from '../../../common/entityId.hooks';
import { WorkflowEntity } from '../../workflow/Workflow.interface';
import { Supplier } from '../Supplier.interface';
import { useFindSuppliers } from '../Supplier.hooks';

const StyledGrid = styled(Grid)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

export interface SupplierSelectorProps {
  value?: number;
  disabled?: boolean;
  onSelected?: (supplier: Supplier) => void;
  onChange?: (supplier: Supplier | null) => void;
}

const SupplierSelector: React.FC<SupplierSelectorProps> = (
  props: SupplierSelectorProps
) => {
  const { value: supplierId, disabled, onChange, onSelected } = props;
  const previousSupplierId = useRef<number | undefined>(supplierId);
  const [search, setSearch] = useState<string>('');
  const [selected, setSelected] = useState<Supplier | undefined>();
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const debouncedSearch: string = useDebounce(search, 300);
  const {
    supplierList: data,
    isLoading,
    loadingError: error,
    findSuppliers
  } = useFindSuppliers();
  const filterOptions = useIdSearchableFilterOptions<Supplier>(
    WorkflowEntity.Supplier,
    'companyName'
  );

  useEffect(() => {
    const params: ApiParams = {};
    if (debouncedSearch) {
      params.search = debouncedSearch;
    }
    findSuppliers(params);
  }, [findSuppliers, debouncedSearch]);

  useEffect(() => {
    data?.data && setSuppliers(data?.data);
  }, [data]);

  // if supplierId changes, reselect supplier
  useEffect(() => {
    if (previousSupplierId.current !== supplierId) {
      setSelected(undefined);
      previousSupplierId.current = supplierId;
    }
  }, [supplierId]);

  useEffect(() => {
    // if no selected supplier yet
    if (selected) {
      return;
    }
    const selectedSupplier: Supplier | undefined = suppliers.find(
      (supplier: Supplier) => supplier.id === supplierId
    );
    if (selectedSupplier) {
      setSelected(selectedSupplier);
      onSelected?.(selectedSupplier);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suppliers, supplierId]);

  return (
    <Autocomplete
      loading={isLoading}
      disabled={disabled}
      value={selected || null}
      options={suppliers}
      filterOptions={filterOptions}
      getOptionLabel={(option: Supplier) => option.companyName}
      getOptionSelected={(option: Supplier) => option.id === supplierId}
      onInputChange={(_, value: string) => setSearch(value)}
      onChange={(_event: React.ChangeEvent<{}>, value: Supplier | null) => {
        if (value) {
          previousSupplierId.current = value.id;
        }
        setSelected(value || undefined);
        onChange?.(value);
      }}
      renderInput={params => (
        <TextField
          {...params}
          fullWidth
          variant='outlined'
          label='Select Supplier'
          placeholder='Choose a Supplier'
          helperText={error?.message}
        />
      )}
      renderOption={(option: Supplier) => (
        <Grid item xs={12}>
          <Grid container spacing={1}>
            <StyledGrid item>
              <Typography variant='body1'>{option.companyName}</Typography>
            </StyledGrid>
            <Grid item>
              <TaskStatusLabel task={option.task} />
            </Grid>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default SupplierSelector;
